import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Share2, Printer, Play, Lock, Unlock, Info } from "lucide-react";
import {
  ROASTS, GRINDS, generateSteps, timeLabel, isEspresso, clamp, round1,
  grindAdvisory, roastSuggestion, POUR_OVER_MAX_SECONDS,
} from "@/lib/brew";
import { shareRecipe, printRecipe } from "@/lib/share";
import { usePrefs } from "@/context/PrefsContext";
import { fmtWeight, fmtVolume, fmtTemp, weightUnitLabel, volumeUnitLabel, tempUnitLabel, toDispWeight, fromDispWeight, toDispVolume, fromDispVolume, toDispTempN, fromDispTempN } from "@/lib/units";

const ROAST_NOTE = {
  "Omni Roast": "Roasted for versatility across multiple brewing methods.",
  "Not Sure": "That’s okay—we’ll start with a balanced recommendation.",
};

export default function RecipeEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editing = location.state?.recipe || null;
  const returnTo = location.state?.returnTo || "/brew-guide";
  const { prefs } = usePrefs();

  const [rec, setRec] = useState(null); // recommended (Wonderbean) recipe
  const [mode, setMode] = useState(editing ? editing.mode : "guided");
  const [savedId, setSavedId] = useState(editing?.id || null);

  const [form, setForm] = useState(null);
  const [lock, setLock] = useState("coffee"); // coffee | water

  useEffect(() => {
    api.get(`/brewers/${slug}`).then(({ data }) => {
      setRec(data);
      if (editing) {
        setForm({
          name: editing.name, brewer: editing.brewer, roast: editing.roast,
          grind: editing.grind, coffee: editing.coffee, water: editing.water,
          yield_g: editing.yield_g, ratio: editing.ratio, temperature: editing.temperature,
          notes: editing.notes || "",
        });
      } else {
        setForm(fromRec(data));
      }
    }).catch(() => toast.error("Could not load brewer"));
  }, [slug]); // eslint-disable-line

  function fromRec(d) {
    return {
      name: d.name, brewer: d.brewer, roast: d.roast, grind: d.grind,
      coffee: d.coffee, water: d.water, yield_g: d.yield_g,
      ratio: d.ratio, temperature: d.temperature, notes: "",
    };
  }

  const esp = form ? isEspresso(form.brewer) : false;

  const { steps, total } = useMemo(() => {
    if (!form) return { steps: [], total: 0 };
    return generateSteps(form.brewer, form.coffee, esp ? form.yield_g : form.water, form.yield_g);
  }, [form, esp]);

  const pourOverTooLong = form && form.brewer.toLowerCase() === "pour over" && total > POUR_OVER_MAX_SECONDS;
  const advisory = form ? grindAdvisory(form.brewer, form.grind) : null;

  if (!form || !rec) {
    return <div><PageHeader title="Loading…" onBack={() => navigate(returnTo)} /></div>;
  }

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const roastSug = roastSuggestion(form.roast, rec.grind, rec.temperature);

  // Linked calculations
  const changeCoffee = (val) => {
    const coffee = clamp(parseFloat(val), 1, 200);
    if (esp) {
      const yieldG = clamp(round1(coffee * form.ratio), 1, 200);
      set({ coffee, yield_g: yieldG });
    } else {
      const water = clamp(round1(coffee * form.ratio), 10, 3000);
      set({ coffee, water });
    }
  };
  const changeWater = (val) => {
    const water = clamp(parseFloat(val), 10, 3000);
    if (lock === "coffee") {
      const ratio = clamp(round1(water / form.coffee), 1, 100);
      set({ water, ratio });
    } else {
      const coffee = clamp(round1(water / form.ratio), 1, 200);
      set({ water, coffee });
    }
  };
  const changeYield = (val) => {
    const yieldG = clamp(parseFloat(val), 1, 200);
    const ratio = clamp(round1(yieldG / form.coffee), 1, 100);
    set({ yield_g: yieldG, ratio });
  };
  const changeRatio = (val) => {
    const ratio = clamp(parseFloat(val), 1, 100);
    if (esp) {
      if (lock === "coffee") set({ ratio, yield_g: clamp(round1(form.coffee * ratio), 1, 200) });
      else set({ ratio, coffee: clamp(round1(form.yield_g / ratio), 1, 200) });
    } else {
      if (lock === "coffee") set({ ratio, water: clamp(round1(form.coffee * ratio), 10, 3000) });
      else set({ ratio, coffee: clamp(round1(form.water / ratio), 1, 200) });
    }
  };
  const changeTemp = (val) => set({ temperature: clamp(parseFloat(val), 40, 100) });

  const applyRecommended = () => {
    setForm((f) => ({ ...fromRec(rec), name: f.name, notes: f.notes }));
    toast.success("Wonderbean recommended settings applied");
  };

  const changeRoast = (roast) => {
    // roast change may update recommendation but must not silently overwrite custom values
    set({ roast });
  };

  const buildRecipe = () => ({
    name: form.name,
    brewer: form.brewer,
    mode,
    roast: form.roast,
    grind: form.grind,
    coffee: form.coffee,
    water: esp ? null : form.water,
    yield_g: esp ? form.yield_g : null,
    ratio: form.ratio,
    temperature: form.temperature,
    notes: form.notes,
    instructions: steps,
    duration: total,
    source_version: rec.version,
  });

  const save = async () => {
    try {
      const payload = buildRecipe();
      if (savedId) {
        await api.put(`/recipes/${savedId}`, payload);
        toast.success("Recipe updated");
      } else {
        const { data } = await api.post("/recipes", payload);
        setSavedId(data.id);
        toast.success("Recipe saved");
      }
    } catch {
      toast.error("Could not save recipe");
    }
  };

  const startBrewing = () => {
    if (pourOverTooLong) return;
    const brew = { id: crypto.randomUUID(), recipe: buildRecipe(), steps, total, status: "preparing", elapsedBase: 0, startedAt: null };
    localStorage.setItem("bm_active_brew", JSON.stringify(brew));
    navigate("/brew");
  };

  const isRecommended = mode === "guided" &&
    form.coffee === rec.coffee &&
    (esp ? form.yield_g === rec.yield_g : form.water === rec.water) &&
    form.ratio === rec.ratio && form.grind === rec.grind &&
    form.temperature === rec.temperature && form.roast === rec.roast;

  return (
    <div data-testid="recipe-editor">
      <PageHeader title={form.brewer} subtitle="Start with our recommendation or make it your own." onBack={() => navigate(returnTo)} />

      {/* Mode tabs */}
      <div className="px-5 pt-4">
        <div className="flex bg-[#2E5E3E] rounded-full p-1">
          {[["guided", "Guided Recipe"], ["custom", "Custom Brew"]].map(([m, label]) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              data-testid={`mode-tab-${m}`}
              className={`flex-1 h-11 rounded-full text-sm font-semibold transition-colors ${mode === m ? "bg-[#F57C00] text-[#1F2A44]" : "text-[#EDE0CC]"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pt-5 space-y-5">
        {mode === "guided" && (
          <div className="rounded-2xl border border-[#EDE0CC]/20 bg-[#2E5E3E] p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-[#EDE0CC]">Wonderbean recommendation</div>
                <div className="text-xs text-[#EDE0CC]/60 mt-0.5">
                  {isRecommended ? "You’re using the recommended recipe." : "You’ve adjusted the recommended recipe."}
                </div>
              </div>
              <Button onClick={applyRecommended} data-testid="use-recommended-btn" className="h-10 rounded-full bg-[#F57C00] text-[#1F2A44] hover:bg-[#E86F00] text-xs">
                Use Recommended Recipe
              </Button>
            </div>
          </div>
        )}

        {mode === "custom" && (
          <Field label="Recipe name">
            <Input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="recipe-name-input" className={inputCls} />
          </Field>
        )}

        {/* Roast profile */}
        <Field label="Roast profile">
          <div className="flex gap-2">
            <Select value={form.roast} onValueChange={changeRoast}>
              <SelectTrigger data-testid="roast-select" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#2E5E3E] border-[#EDE0CC]/20 text-[#EDE0CC]">
                {ROASTS.map((r) => <SelectItem key={r} value={r} data-testid={`roast-${r}`}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={applyRecommended} data-testid="apply-suggested-btn" className="h-12 shrink-0 rounded-xl bg-[#F57C00] text-[#1F2A44] hover:bg-[#E86F00] text-xs px-3">
              Apply Suggested
            </Button>
          </div>
          {ROAST_NOTE[form.roast] && (
            <p className="mt-2 text-xs text-[#1F2A44]/70 flex gap-1.5"><Info size={14} className="shrink-0 mt-0.5" />{ROAST_NOTE[form.roast]}</p>
          )}
          {(roastSug.grind !== form.grind || roastSug.temperature !== form.temperature) && (
            <button type="button" onClick={() => set({ grind: roastSug.grind, temperature: roastSug.temperature })} data-testid="apply-roast-suggestion" className="mt-2 rounded-full bg-[#F57C00] px-3 py-2 text-left text-xs font-semibold text-[#1F2A44]">
              Suggested for {form.roast}: {roastSug.grind} grind, {fmtTemp(roastSug.temperature, prefs)} — tap to apply grind &amp; temp
            </button>
          )}
        </Field>

        {/* Grind */}
        <Field label="Grind size">
          <Select value={form.grind} onValueChange={(g) => set({ grind: g })}>
            <SelectTrigger data-testid="grind-select" className={inputCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#2E5E3E] border-[#EDE0CC]/20 text-[#EDE0CC]">
              {GRINDS.map((g) => <SelectItem key={g} value={g} data-testid={`grind-${g}`}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
          {advisory && <p className="mt-2 text-xs text-[#1F2A44]/70 flex gap-1.5"><Info size={14} className="shrink-0 mt-0.5" />{advisory}</p>}
        </Field>

        {/* Coffee / water/yield / ratio */}
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Coffee (${weightUnitLabel(prefs)})`}>
            <Input type="number" inputMode="decimal" value={toDispWeight(form.coffee, prefs)} onChange={(e) => changeCoffee(fromDispWeight(e.target.value === "" ? 0 : parseFloat(e.target.value), prefs))} data-testid="coffee-input" className={inputCls} />
          </Field>
          {esp ? (
            <Field label={`Espresso yield (${weightUnitLabel(prefs)})`}>
              <Input type="number" inputMode="decimal" value={form.yield_g == null ? "" : toDispWeight(form.yield_g, prefs)} onChange={(e) => changeYield(fromDispWeight(e.target.value === "" ? 0 : parseFloat(e.target.value), prefs))} data-testid="yield-input" className={inputCls} />
            </Field>
          ) : (
            <Field label={`Water (${volumeUnitLabel(prefs)})`}>
              <Input type="number" inputMode="decimal" value={toDispVolume(form.water, prefs)} onChange={(e) => changeWater(fromDispVolume(e.target.value === "" ? 0 : parseFloat(e.target.value), prefs))} data-testid="water-input" className={inputCls} />
            </Field>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Ratio (1 : x)">
            <Input type="number" inputMode="decimal" value={form.ratio} onChange={(e) => changeRatio(e.target.value)} data-testid="ratio-input" className={inputCls} />
          </Field>
          <Field label={`Temperature (${tempUnitLabel(prefs)})`}>
            <Input type="number" inputMode="decimal" value={toDispTempN(form.temperature, prefs)} onChange={(e) => changeTemp(fromDispTempN(e.target.value === "" ? 0 : parseFloat(e.target.value), prefs))} data-testid="temp-input" className={inputCls} />
          </Field>
        </div>

        {prefs && (prefs.temp_unit !== "C" || prefs.weight_unit !== "g" || prefs.volume_unit !== "ml") && (
          <p className="text-xs text-[#1F2A44]/60" data-testid="unit-helper-note">Values are shown in your preferred units and stored precisely. Change units in Account → Brewing Preferences.</p>
        )}

        {/* Lock toggle */}
        <button
          onClick={() => setLock(lock === "coffee" ? "water" : "coffee")}
          data-testid="lock-toggle"
          className="flex items-center gap-2 rounded-full bg-[#F57C00] px-3 py-2 text-xs font-semibold text-[#1F2A44] active:scale-95 transition-transform"
        >
          {lock === "coffee" ? <Lock size={14} /> : <Unlock size={14} />}
          Keeping <span className="font-semibold">{lock}</span> fixed when changing ratio (tap to switch)
        </button>

        <Field label="Recipe notes">
          <textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="notes-input" rows={3}
            className="w-full rounded-xl bg-[#234A31] border border-[#EDE0CC]/30 text-[#EDE0CC] p-3 text-sm placeholder:text-[#EDE0CC]/40" placeholder="Add preparation tips, adjustments or anything you want to remember about this recipe." />
        </Field>

        {/* Live summary */}
        <div className="rounded-2xl border border-[#EDE0CC]/20 bg-[#2E5E3E] p-4" data-testid="recipe-summary">
          <div className="text-xs uppercase tracking-wide text-[#EDE0CC]/50 mb-2">Live recipe summary</div>
          <div className="grid grid-cols-2 gap-y-1.5 text-sm text-[#EDE0CC]/90">
            <span>Coffee</span><span className="text-right font-semibold">{fmtWeight(form.coffee, prefs)}</span>
            {esp ? (<><span>Yield</span><span className="text-right font-semibold">{fmtWeight(form.yield_g, prefs)}</span></>)
                 : (<><span>Water</span><span className="text-right font-semibold">{fmtVolume(form.water, prefs)}</span></>)}
            <span>Ratio</span><span className="text-right font-semibold">1:{form.ratio}</span>
            <span>Grind</span><span className="text-right font-semibold">{form.grind}</span>
            <span>Temp</span><span className="text-right font-semibold">{fmtTemp(form.temperature, prefs)}</span>
            <span>Total time</span><span className="text-right font-semibold">{timeLabel(total)}</span>
          </div>
        </div>

        {pourOverTooLong && (
          <div className="rounded-2xl border border-[#E56B6B]/50 bg-[#E56B6B]/10 p-4 text-sm text-[#EDE0CC]" data-testid="pour-over-limit-warning">
            This Pour Over recipe is too large for BrewMate’s 3:30 brewing limit. Reduce the amount or prepare it in separate batches.
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-3 gap-2">
          <ActionBtn onClick={save} icon={Save} label="Save" testid="save-recipe-btn" />
          <ActionBtn onClick={() => shareRecipe(buildRecipe())} icon={Share2} label="Share" testid="share-recipe-btn" />
          <ActionBtn onClick={() => printRecipe(buildRecipe())} icon={Printer} label="Print" testid="print-recipe-btn" />
        </div>

        <Button
          onClick={startBrewing}
          disabled={pourOverTooLong}
          data-testid="start-brew-btn"
          className="w-full h-14 rounded-full bg-[#F57C00] text-[#1F2A44] text-base font-semibold hover:bg-[#E86F00] active:scale-95 transition-all disabled:opacity-40 flex items-center justify-center gap-2"
        >
          <Play size={20} /> Start Brewing
        </Button>
        <div className="h-4" />
      </div>
    </div>
  );
}

const inputCls = "h-12 rounded-xl bg-[#234A31] border-[#EDE0CC]/30 text-[#EDE0CC] focus:border-[#EDE0CC] focus:ring-1 focus:ring-[#EDE0CC]";

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-[#1F2A44]/70">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex flex-col items-center gap-1 h-16 rounded-2xl border border-[#F57C00] bg-[#F57C00] text-[#1F2A44] active:scale-95 transition-transform hover:bg-[#E86F00]">
      <Icon size={18} />
      <span className="text-xs font-semibold">{label}</span>
    </button>
  );
}
